import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Database, Download, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import * as db from "@/lib/db";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MIGRATION_FLAG_KEY = "gaochill-mongo-migrated";

const MigrationTool = () => {
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [log, setLog] = useState([]);

  const addLog = (msg) => setLog((l) => [...l, msg]);

  const alreadyMigrated = window.localStorage.getItem(MIGRATION_FLAG_KEY) === "true";

  const handleScan = async () => {
    setScanning(true);
    setPreview(null);
    setLog([]);
    try {
      const [productsRes, categoriesRes, unitsRes, eventsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/units`),
        axios.get(`${API}/events`),
        axios.get(`${API}/reconciliation-settings`),
      ]);
      const result = {
        products: productsRes.data,
        categories: categoriesRes.data,
        units: unitsRes.data,
        events: eventsRes.data,
        settings: settingsRes.data,
      };
      setPreview(result);
      addLog(`Found ${result.products.length} products, ${result.categories.length} categories, ${result.units.length} units, ${result.events.length} events.`);
    } catch (err) {
      console.error(err);
      toast.error("Unable to reach legacy backend. Local data may still be imported from browser storage below.");
    } finally {
      setScanning(false);
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    setShowConfirm(false);

    try {
      // Load existing supabase data to avoid duplicates
      const [existingCats, existingUnits, existingProducts, existingEvents] = await Promise.all([
        db.listCategories(),
        db.listUnits(),
        db.listProducts(),
        db.listEvents(),
      ]);
      const existingCatNames = new Set(existingCats.map((c) => c.name.toLowerCase()));
      const existingUnitNames = new Set(existingUnits.map((u) => u.name.toLowerCase()));
      const existingProdKey = new Set(existingProducts.map((p) => `${p.name}|${p.category}|${p.unit}|${p.unitCost}`));
      const existingEventKey = new Set(existingEvents.map((e) => `${e.eventName}|${e.startDate}|${e.endDate}`));

      // Categories
      let catCount = 0;
      for (const c of preview.categories) {
        if (!existingCatNames.has(c.name.toLowerCase())) {
          try {
            await db.createCategory(c.name);
            catCount++;
          } catch (e) { /* likely already exists */ }
        }
      }
      addLog(`✓ Imported ${catCount} new categories`);

      // Units
      let unitCount = 0;
      for (const u of preview.units) {
        if (!existingUnitNames.has(u.name.toLowerCase())) {
          try {
            await db.createUnit(u.name);
            unitCount++;
          } catch (e) { /* likely already exists */ }
        }
      }
      addLog(`✓ Imported ${unitCount} new units`);

      // Products (build ID mapping mongoId -> supabaseId)
      const productIdMap = {};
      let prodCount = 0;
      for (const p of preview.products) {
        const key = `${p.name}|${p.category}|${p.unit}|${p.unitCost}`;
        if (existingProdKey.has(key)) continue;
        try {
          const created = await db.createProduct({
            name: p.name,
            category: p.category,
            unit: p.unit,
            unitCost: p.unitCost,
            defaultPrice: p.defaultPrice,
            isActive: p.isActive,
            remark: p.remark,
          });
          productIdMap[p.id] = created.id;
          prodCount++;
        } catch (e) {
          console.warn("Failed to import product", p.name, e);
        }
      }
      // Also map already-existing products by (name+category+unit+cost)
      const allProdsNow = await db.listProducts();
      const prodByKey = {};
      allProdsNow.forEach((p) => (prodByKey[`${p.name}|${p.category}|${p.unit}|${p.unitCost}`] = p.id));
      preview.products.forEach((p) => {
        const key = `${p.name}|${p.category}|${p.unit}|${p.unitCost}`;
        if (prodByKey[key] && !productIdMap[p.id]) productIdMap[p.id] = prodByKey[key];
      });
      addLog(`✓ Imported ${prodCount} new products`);

      // Settings
      try {
        await db.updateReconciliationSettings({
          standardCupSellingPrice: preview.settings.standardCupSellingPrice,
          conservativeCupUnitCost: preview.settings.conservativeCupUnitCost,
          remark: preview.settings.remark,
        });
        addLog(`✓ Reconciliation settings synchronised`);
      } catch (e) {
        console.warn("Settings import warning:", e);
      }

      // Events
      let eventCount = 0;
      for (const ev of preview.events) {
        const key = `${ev.eventName}|${ev.startDate}|${ev.endDate}`;
        if (existingEventKey.has(key)) continue;
        try {
          // Remap product IDs inside event.products
          const remappedProducts = (ev.products || []).map((p) => ({
            ...p,
            productId: productIdMap[p.productId] || p.productId,
          }));
          await db.createEvent({
            ...ev,
            products: remappedProducts,
            staffWages: ev.staffWages || [],
          });
          eventCount++;
        } catch (e) {
          console.warn("Failed to import event", ev.eventName, e);
        }
      }
      addLog(`✓ Imported ${eventCount} new events`);

      window.localStorage.setItem(MIGRATION_FLAG_KEY, "true");
      setImported(true);
      toast.success("Existing local data imported successfully.");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="migration-title">
            Import Existing Local Data
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            One-time migration from the legacy backend into Supabase. Duplicate records are skipped automatically.
          </p>
        </div>

        {alreadyMigrated && !imported ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3" data-testid="already-migrated">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900">Migration already completed on this device</p>
              <p className="text-sm text-emerald-800 mt-1">
                A migration marker was found in local storage. You can still re-scan and import — duplicates will be skipped.
              </p>
            </div>
          </div>
        ) : null}

        <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#193f7d]/10 flex items-center justify-center">
              <Database className="w-5 h-5 text-[#193f7d]" />
            </div>
            <div>
              <h2 className="font-medium text-slate-900">Step 1 — Scan legacy backend</h2>
              <p className="text-sm text-slate-500">Detect existing products, categories, units, and events</p>
            </div>
          </div>
          <Button
            onClick={handleScan}
            disabled={scanning}
            className="bg-[#193f7d] hover:bg-[#122f5e] text-white rounded-full h-11 px-6 font-semibold"
            data-testid="scan-btn"
          >
            {scanning ? "Scanning..." : "Scan now"}
          </Button>

          {preview && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="preview-counts">
              <PreviewBox label="Categories" value={preview.categories.length} />
              <PreviewBox label="Units" value={preview.units.length} />
              <PreviewBox label="Products" value={preview.products.length} />
              <PreviewBox label="Events" value={preview.events.length} />
            </div>
          )}
        </div>

        {preview && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#fa9d1c]/10 flex items-center justify-center">
                <Download className="w-5 h-5 text-[#fa9d1c]" />
              </div>
              <div>
                <h2 className="font-medium text-slate-900">Step 2 — Import into Supabase</h2>
                <p className="text-sm text-slate-500">Duplicates will be skipped. Snapshots are preserved.</p>
              </div>
            </div>
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={importing || imported}
              className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-11 px-6 font-semibold"
              data-testid="import-btn"
            >
              {importing ? "Importing..." : imported ? "Imported ✓" : "Import into Supabase"}
            </Button>
          </div>
        )}

        {log.length > 0 && (
          <div className="bg-slate-900 text-slate-100 rounded-2xl p-5 font-mono text-xs whitespace-pre-wrap" data-testid="migration-log">
            {log.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Confirm import
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will copy {preview?.products.length || 0} products, {preview?.categories.length || 0} categories, {preview?.units.length || 0} units, and {preview?.events.length || 0} events into your Supabase project. Duplicates are skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-full h-11 px-6 bg-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleImport}
              className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-11 px-6 font-semibold"
              data-testid="confirm-import-btn"
            >
              Yes, import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const PreviewBox = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-mono font-semibold text-slate-900 text-xl mt-1">{value}</p>
  </div>
);

export default MigrationTool;
